import { useState, useEffect } from "react";
import { toast } from "sonner";
import { PaymentPanel } from "@/components/PaymentPanel";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, Search, Upload, Phone } from "lucide-react";
import { SignaturePad } from "@/components/SignaturePad";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputL =
  "w-full border border-zinc-300 bg-white px-4 py-3.5 text-sm text-slate-900 placeholder-zinc-400 outline-none transition-colors focus:border-blaze";
const labelL = "mb-2 block text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500";

const Brand = () => (
  <a href="/" className="flex items-center gap-2.5" data-testid="signoff-brand">
    <img src="/images/logo-full.png" alt="MoversNearby" className="h-9 w-auto" />
  </a>
);

const DetailRow = ({ k, v }) => (
  <div className="flex items-start justify-between gap-6 border-b border-zinc-200 py-3.5 last:border-b-0">
    <span className="text-xs uppercase tracking-widest text-slate-500">{k}</span>
    <span className="text-right text-sm font-semibold text-slate-900">{v}</span>
  </div>
);

export default function CompleteJob() {
  const [jobId, setJobId] = useState("");
  const [job, setJob] = useState(null);
  const [lookupError, setLookupError] = useState("");
  const [looking, setLooking] = useState(false);
  const [noDamage, setNoDamage] = useState(false);
  const [reportDamage, setReportDamage] = useState(false);
  const [damageDesc, setDamageDesc] = useState("");
  const [damagePhoto, setDamagePhoto] = useState(null);
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [moverName, setMoverName] = useState("");
  const [customerSig, setCustomerSig] = useState(null);
  const [moverSig, setMoverSig] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [hours, setHours] = useState({});
  const [savingHours, setSavingHours] = useState(false);
  const [hoursSaved, setHoursSaved] = useState(false);
  const [actualHours, setActualHours] = useState("");
  const [extraPayment, setExtraPayment] = useState("");
  const [extraNotes, setExtraNotes] = useState("");
  const [stairs, setStairs] = useState("");

  const saveHours = async () => {
    setError("");
    const mh = {};
    for (const [k, v] of Object.entries(hours)) {
      const n = parseFloat(v);
      if (!Number.isFinite(n) || n < 0) {
        setError(`Enter valid hours for ${k}.`);
        return;
      }
      mh[k] = n;
    }
    if (actualHours !== "" && (!Number.isFinite(parseFloat(actualHours)) || parseFloat(actualHours) < 0)) {
      setError("Enter valid travel hours.");
      return;
    }
    if (extraPayment !== "" && (!Number.isFinite(parseFloat(extraPayment)) || parseFloat(extraPayment) < 0)) {
      setError("Enter a valid extra payment amount.");
      return;
    }
    setSavingHours(true);
    try {
      await axios.post(`${API}/jobs/${job.job_id}/hours`, {
        mover_hours: mh,
        ...(actualHours !== "" ? { travel_hours: parseFloat(actualHours) } : {}),
        ...(extraPayment !== "" ? { extra_payment: parseFloat(extraPayment) } : {}),
        ...(extraNotes.trim() ? { notes: extraNotes.trim() } : {}),
        ...(stairs.trim() ? { stairs: stairs.trim() } : {}),
      });
      setHoursSaved(true);
      window.scrollTo({ top: 0 });
    } catch {
      setError("Could not save hours. Please try again.");
    } finally {
      setSavingHours(false);
    }
  };
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [finished, setFinished] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [paymentStage, setPaymentStage] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [manualError, setManualError] = useState("");
  const [mCustomer, setMCustomer] = useState("");
  const [mPickup, setMPickup] = useState("");
  const [mDrop, setMDrop] = useState("");
  const [mDate, setMDate] = useState("");

  const startManual = () => {
    if (!mCustomer.trim() || !mPickup.trim() || !mDrop.trim() || !mDate) {
      setManualError("Please fill in all job details.");
      return;
    }
    setJob({
      job_id: jobId.trim().toUpperCase() || "YM-MANUAL",
      customer_name: mCustomer,
      pickup_address: mPickup,
      drop_address: mDrop,
      moving_date: mDate,
      start_time: null,
      truck: null,
      manual: true,
    });
    setCustomerName(mCustomer);
  };

  const lookup = async (e, override) => {
    if (e) e.preventDefault();
    const target = (override || jobId).trim();
    if (!target) return;
    setLooking(true);
    setLookupError("");
    try {
      const { data } = await axios.get(`${API}/jobs/${encodeURIComponent(target)}`);
      setJob(data);
      setCustomerName(data.customer_name);
      setCustomerEmail(data.email || "");
      if (data.work_hours && Object.keys(data.work_hours).length) {
        setHours(Object.fromEntries(Object.entries(data.work_hours).map(([k, v]) => [k, String(v)])));
      }
      if (!data.completed) {
        try {
          const [{ data: payInfo }, { data: settings }] = await Promise.all([
            axios.get(`${API}/jobs/${data.job_id}/payment`),
            axios.get(`${API}/settings/payments`),
          ]);
          if (settings.mover_payments_enabled && !payInfo.payment) setPaymentStage(true);
        } catch { /* payment info unavailable — continue to sign-off */ }
      }
    } catch (err) {
      setLookupError(err.response?.data?.detail || "Job not found. Check your Job ID and try again.");
    } finally {
      setLooking(false);
    }
  };

  const [searchParams] = useSearchParams();
  useEffect(() => {
    const j = searchParams.get("job");
    const sid = searchParams.get("session_id");
    const cancelled = searchParams.get("payment") === "cancelled";
    if (sid && j) {
      // Returning from Stripe checkout — confirm the payment before sign-off
      setCheckingPayment(true);
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts += 1;
        try {
          const { data } = await axios.get(`${API}/payments/status/${sid}`);
          if (data.status === "collected") {
            clearInterval(poll);
            toast.success(`Card payment received — $${data.total}. Invoice emailed to the customer.`);
            setCheckingPayment(false);
            setJobId(j);
            lookup(null, j);
          } else if (attempts > 10) {
            clearInterval(poll);
            setCheckingPayment(false);
            toast.error("Payment is still processing. If it was charged, the office can see it in the job record.");
            setJobId(j);
            lookup(null, j);
          }
        } catch {
          if (attempts > 10) {
            clearInterval(poll);
            setCheckingPayment(false);
            setJobId(j);
            lookup(null, j);
          }
        }
      }, 2000);
      return () => clearInterval(poll);
    }
    if (cancelled) toast.error("Card payment was cancelled. You can try again or choose another method.");
    if (j) {
      setJobId(j);
      lookup(null, j);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      setError("Photo must be under 3MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setDamagePhoto(reader.result);
    reader.readAsDataURL(file);
  };

  const onPhotos = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 4 - photos.length);
    files.forEach((f) => {
      if (f.size > 3 * 1024 * 1024) {
        setError("Each photo must be under 3MB.");
        return;
      }
      const reader = new FileReader();
      reader.onload = () => setPhotos((p) => [...p, reader.result].slice(0, 4));
      reader.readAsDataURL(f);
    });
    e.target.value = "";
  };

  const submit = async () => {
    setError("");
    if (!noDamage && !reportDamage) return setError("Please complete the damage confirmation.");
    if (reportDamage && !damageDesc.trim()) return setError("Please describe the damage.");
    if (!customerName.trim() || !moverName.trim()) return setError("Please enter both names.");
    if (!customerSig || !moverSig) return setError("Both signatures are required.");
    setSubmitting(true);
    try {
      await axios.post(`${API}/jobs/${job.job_id}/complete`, {
        no_damage: noDamage,
        report_damage: reportDamage,
        damage_description: reportDamage ? damageDesc : null,
        damage_photo: reportDamage ? damagePhoto : null,
        customer_name: customerName,
        mover_name: moverName,
        customer_signature: customerSig,
        mover_signature: moverSig,
        customer_email: customerEmail || null,
        photos,
        ...(job.manual ? { manual_pickup: job.pickup_address, manual_drop: job.drop_address, manual_date: job.moving_date } : {}),
      });
      setFinished(true);
      window.scrollTo({ top: 0 });
    } catch (err) {
      setError(err.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#f4f1ea]" data-testid="complete-job-page">
      <div className="border-b border-zinc-200 bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-4">
          <Brand />
          <a href="tel:0391136005" className="flex items-center gap-2 text-sm font-semibold text-slate-500 transition-colors hover:text-blaze" data-testid="signoff-call">
            <Phone className="h-4 w-4 text-blaze" /> 03 9113 6005
          </a>
        </div>
      </div>

      <section className="mx-auto max-w-3xl px-5 py-12 lg:py-16">
        {checkingPayment ? (
          <div className="border border-zinc-200 bg-white p-10 text-center shadow-sm lg:p-16" data-testid="payment-checking">
            <Loader2 className="mx-auto mb-6 h-12 w-12 animate-spin text-blaze" strokeWidth={1.4} />
            <h2 className="font-display text-3xl font-semibold uppercase tracking-tighter text-slate-900 sm:text-4xl">Confirming payment…</h2>
            <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-slate-500">Please wait while we confirm your card payment with the bank.</p>
          </div>
        ) : paymentStage && job && !finished ? (
          <PaymentPanel job={job} onDone={() => { setPaymentStage(false); window.scrollTo({ top: 0 }); }} />
        ) : finished && !hoursSaved && (job.assigned_movers || []).length > 0 ? (
          <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} className="border border-zinc-200 bg-white p-8 shadow-sm lg:p-12" data-testid="hours-entry">
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.3em] text-blaze">Job {job.job_id} Signed Off</p>
            <h2 className="font-display text-3xl font-semibold uppercase tracking-tighter text-slate-900 sm:text-4xl">Log mover hours</h2>
            <p className="mt-3 max-w-lg text-sm text-slate-500">
              The customer has been emailed the completion form and review link. Now enter the
              hours each mover worked — pay is calculated from these hours.
            </p>
            {job.work_hours && Object.keys(job.work_hours).length > 0 && (
              <p className="mt-3 max-w-lg rounded-lg bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700" data-testid="hours-from-timer-note">
                These hours are pre-filled from the job timer. If they're a little off, just edit the numbers below.
              </p>
            )}
            <div className="mt-8 grid gap-5 sm:grid-cols-2">
              {(job.assigned_movers || []).map((m) => (
                <div key={m}>
                  <label className={labelL}>{m} — hours worked</label>
                  <input
                    type="number" min="0" step="0.01" className={inputL}
                    value={hours[m] ?? ""}
                    onChange={(e) => setHours({ ...hours, [m]: e.target.value })}
                    data-testid={`hours-input-${m.toLowerCase()}`}
                  />
                </div>
              ))}
            </div>
            <div className="mt-8 border-t border-zinc-200 pt-6" data-testid="signoff-extras">
              <p className="text-xs text-slate-500">
                The details below are visible to the office (admin) only — the customer never sees them.
              </p>
              <div className="mt-4 grid gap-5 sm:grid-cols-2">
                <div>
                  <label className={labelL}>Travel hours (if given for this job)</label>
                  <input
                    type="number" min="0" step="0.5" className={inputL}
                    value={actualHours}
                    onChange={(e) => setActualHours(e.target.value)}
                    placeholder="e.g. 0.5"
                    data-testid="travel-hours-input"
                  />
                </div>
                <div>
                  <label className={labelL}>Extra payment collected ($)</label>
                  <input
                    type="number" min="0" step="1" className={inputL}
                    value={extraPayment}
                    onChange={(e) => setExtraPayment(e.target.value)}
                    placeholder="e.g. 120"
                    data-testid="extra-payment-input"
                  />
                </div>
              </div>
              <div className="mt-5">
                <label className={labelL}>Stairs (flights at pickup / drop-off)</label>
                <input
                  type="text"
                  className={inputL}
                  value={stairs}
                  onChange={(e) => setStairs(e.target.value)}
                  placeholder="e.g. Pickup — 2 flights, Drop-off — ground floor"
                  data-testid="signoff-stairs-input"
                />
              </div>
              <div className="mt-5">
                <label className={labelL}>Notes / comments (explain any extra charges)</label>
                <textarea
                  rows={3}
                  className={`${inputL} resize-none`}
                  value={extraNotes}
                  onChange={(e) => setExtraNotes(e.target.value)}
                  placeholder="e.g. Extra $120 collected for 3 flights of stairs and heavy piano…"
                  data-testid="signoff-notes-input"
                />
              </div>
            </div>
            {error && <p className="mt-5 text-sm font-semibold text-blaze" data-testid="hours-error">{error}</p>}
            <div className="mt-8 flex flex-wrap gap-4">
              <button
                onClick={saveHours}
                disabled={savingHours}
                className="flex items-center gap-2 bg-blaze px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white disabled:opacity-50"
                data-testid="save-hours-btn"
              >
                {savingHours ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Hours"}
              </button>
              <button onClick={() => setHoursSaved(true)} className="border border-zinc-300 px-6 py-4 text-xs font-bold uppercase tracking-widest text-slate-500 hover:border-blaze hover:text-blaze" data-testid="skip-hours-btn">
                Skip for now
              </button>
            </div>
          </motion.div>
        ) : finished ? (
          <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} className="border border-zinc-200 bg-white p-10 text-center shadow-sm lg:p-16" data-testid="completion-success">
            <CheckCircle2 className="mx-auto mb-6 h-16 w-16 text-green-600" strokeWidth={1.4} />
            <h2 className="font-display text-3xl font-semibold uppercase tracking-tighter text-slate-900 sm:text-4xl">Thank you</h2>
            <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-slate-500">
              Your move has been successfully completed. A copy of the completed job form
              and a review link have been emailed to the customer.{hoursSaved && " Mover hours are saved."}
            </p>
            <a href="/" className="mt-8 inline-block bg-blaze px-8 py-3.5 text-xs font-bold uppercase tracking-widest text-white" data-testid="completion-home-btn">Back to Home</a>
          </motion.div>
        ) : !job ? (
          <div className="border border-zinc-200 bg-white p-8 shadow-sm lg:p-12">
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.3em] text-blaze">Move Completion &amp; Sign-Off</p>
            <h1 className="font-display text-4xl font-semibold uppercase tracking-tighter text-slate-900 sm:text-5xl" data-testid="completion-heading">
              Complete your <span className="text-blaze">job</span>
            </h1>
            <p className="mt-4 max-w-lg text-sm text-slate-500">Enter the Job ID from your booking confirmation (e.g. YM-10254).</p>
            <form onSubmit={lookup} className="mt-10 flex gap-3" data-testid="job-lookup-form">
              <input
                className={`${inputL} max-w-xs font-display text-lg tracking-wider`}
                placeholder="YM-00000"
                value={jobId}
                onChange={(e) => setJobId(e.target.value)}
                data-testid="job-id-input"
              />
              <button type="submit" disabled={looking || !jobId.trim()} className="flex items-center gap-2 bg-blaze px-6 text-xs font-bold uppercase tracking-widest text-white disabled:opacity-50" data-testid="job-lookup-btn">
                {looking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Find Job
              </button>
            </form>
            {lookupError && <p className="mt-4 text-sm font-semibold text-blaze" data-testid="job-lookup-error">{lookupError}</p>}
            {lookupError && !manualMode && (
              <div className="mt-6 border-t border-zinc-200 pt-6">
                <p className="text-sm text-slate-500">Can&rsquo;t find your job? Enter the details manually instead.</p>
                <button
                  onClick={() => setManualMode(true)}
                  className="mt-3 border border-blaze px-5 py-3 text-xs font-bold uppercase tracking-widest text-blaze transition-colors hover:bg-blaze hover:text-white"
                  data-testid="manual-entry-btn"
                >
                  Enter Details Manually
                </button>
              </div>
            )}
            {manualMode && (
              <div className="mt-6 space-y-4 border-t border-zinc-200 pt-6" data-testid="manual-entry-form">
                <div>
                    <label className={labelL}>Customer Name *</label>
                    <input className={inputL} value={mCustomer} onChange={(e) => setMCustomer(e.target.value)} placeholder="Full name" data-testid="manual-customer-input" />
                  </div>
                  <div>
                    <label className={labelL}>Customer Email (for confirmation copy)</label>
                    <input type="email" className={inputL} value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} placeholder="customer@email.com" data-testid="manual-email-input" />
                  </div>
                  <div>
                    <label className={labelL}>Pickup Address *</label>
                    <input className={inputL} value={mPickup} onChange={(e) => setMPickup(e.target.value)} placeholder="Street address, suburb" data-testid="manual-pickup-input" />
                  </div>
                  <div>
                    <label className={labelL}>Drop-off Address *</label>
                    <input className={inputL} value={mDrop} onChange={(e) => setMDrop(e.target.value)} placeholder="Street address, suburb" data-testid="manual-drop-input" />
                  </div>
                  <div>
                    <label className={labelL}>Moving Date *</label>
                    <input type="date" className={inputL} value={mDate} onChange={(e) => setMDate(e.target.value)} data-testid="manual-date-input" />
                  </div>
                  {manualError && <p className="text-sm font-semibold text-blaze" data-testid="manual-entry-error">{manualError}</p>}
                  <button onClick={startManual} className="bg-blaze px-8 py-3.5 text-xs font-bold uppercase tracking-widest text-white" data-testid="manual-continue-btn">
                    Continue
                  </button>
              </div>
            )}
          </div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="border border-zinc-200 bg-white shadow-sm">
            <div className="border-b border-zinc-200 p-8 lg:p-10">
              <p className="mb-3 text-xs font-bold uppercase tracking-[0.3em] text-blaze">Step 1 — Job Details</p>
              <h1 className="font-display text-3xl font-semibold uppercase tracking-tighter [word-spacing:0.14em] text-slate-900 sm:text-4xl">Job {job.job_id}</h1>
              <div className="mt-8 border border-zinc-200 bg-zinc-50 p-6" data-testid="job-details-card">
                <DetailRow k="Customer" v={job.customer_name} />
                <DetailRow k="Pickup" v={job.pickup_address} />
                <DetailRow k="Drop-off" v={job.drop_address} />
                <DetailRow k="Moving Date" v={job.start_time ? `${job.moving_date} — ${job.start_time}` : job.moving_date} />
                {job.truck && <DetailRow k="Truck" v={job.truck} />}
              </div>
            </div>

            {job.completed ? (
              <p className="m-8 border border-green-600/30 bg-green-50 p-5 text-sm font-semibold text-green-700" data-testid="job-already-completed">
                This job has already been signed off as complete.
              </p>
            ) : (
              <>
                <div className="border-b border-zinc-200 p-8 lg:p-10">
                  <p className="mb-3 text-xs font-bold uppercase tracking-[0.3em] text-blaze">Step 2 — Customer Confirmation</p>
                  <p className="text-sm leading-relaxed text-slate-700" data-testid="completion-confirm-text">
                    I confirm that the movers have completed the move and that the goods
                    were moved to the agreed destination.
                  </p>
                  <div className="mt-6 space-y-4">
                    <label className="flex cursor-pointer items-start gap-3 text-sm text-slate-700" data-testid="no-damage-label">
                      <input type="checkbox" checked={noDamage} onChange={(e) => { setNoDamage(e.target.checked); if (e.target.checked) setReportDamage(false); }} className="mt-0.5 h-4 w-4 accent-[#2563eb]" data-testid="no-damage-check" />
                      I confirm that I have inspected my belongings and, to the best of my knowledge, there is no new damage caused during the move.
                    </label>
                    <label className="flex cursor-pointer items-start gap-3 text-sm text-slate-700" data-testid="report-damage-label">
                      <input type="checkbox" checked={reportDamage} onChange={(e) => { setReportDamage(e.target.checked); if (e.target.checked) setNoDamage(false); }} className="mt-0.5 h-4 w-4 accent-[#2563eb]" data-testid="report-damage-check" />
                      I would like to report damage.
                    </label>
                  </div>
                  {reportDamage && (
                    <div className="mt-6 space-y-5 border-t border-zinc-200 pt-6" data-testid="damage-form">
                      <div>
                        <label className={labelL}>Damage Description *</label>
                        <textarea rows={3} className={`${inputL} resize-none`} value={damageDesc} onChange={(e) => setDamageDesc(e.target.value)} placeholder="Describe the damage…" data-testid="damage-desc-input" />
                      </div>
                      <div>
                        <label className={labelL}>Photo (optional, max 3MB)</label>
                        <label className="flex cursor-pointer items-center gap-3 border border-dashed border-zinc-300 px-5 py-4 text-sm text-slate-500 transition-colors hover:border-blaze" data-testid="damage-photo-label">
                          <Upload className="h-4 w-4 text-blaze" />
                          {damagePhoto ? "Photo attached" : "Upload a photo"}
                          <input type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="damage-photo-input" />
                        </label>
                      </div>
                    </div>
                  )}
                </div>

                <div className="border-b border-zinc-200 p-8 lg:p-10">
                  <p className="mb-3 text-xs font-bold uppercase tracking-[0.3em] text-blaze">Step 3 — Job Photos</p>
                  <p className="text-sm text-slate-500">Upload photos of the completed job (optional, up to 4, max 3MB each).</p>
                  <label className="mt-5 flex cursor-pointer items-center justify-center gap-3 border border-dashed border-zinc-300 px-5 py-6 text-sm text-slate-500 transition-colors hover:border-blaze" data-testid="job-photos-label">
                    <Upload className="h-5 w-5 text-blaze" />
                    {photos.length ? `${photos.length} photo${photos.length === 1 ? "" : "s"} attached` : "Upload job photos"}
                    <input type="file" accept="image/*" multiple className="hidden" onChange={onPhotos} data-testid="job-photos-input" />
                  </label>
                  {photos.length > 0 && (
                    <div className="mt-4 grid grid-cols-4 gap-2" data-testid="job-photos-preview">
                      {photos.map((p, i) => (
                        <div key={i} className="relative">
                          <img src={p} alt={`Job ${i + 1}`} className="h-20 w-full border border-zinc-200 object-cover" />
                          <button type="button" onClick={() => setPhotos(photos.filter((_, x) => x !== i))} className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center bg-blaze text-[10px] font-bold text-white" data-testid={`photo-remove-${i}`}>×</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="p-8 lg:p-10">
                  <p className="mb-3 text-xs font-bold uppercase tracking-[0.3em] text-blaze">Step 4 — Signatures</p>
                  <div className="grid gap-8 sm:grid-cols-2">
                    <div>
                      <SignaturePad label="Mover Signature" onChange={setMoverSig} testid="mover-signature" />
                      <input className={`${inputL} mt-4`} placeholder="Mover name" value={moverName} onChange={(e) => setMoverName(e.target.value)} data-testid="mover-name-input" />
                    </div>
                    <div>
                      <SignaturePad label="Customer Signature" onChange={setCustomerSig} testid="customer-signature" />
                      <input className={`${inputL} mt-4`} placeholder="Customer name" value={customerName} onChange={(e) => setCustomerName(e.target.value)} data-testid="customer-name-input" />
                      <input className={`${inputL} mt-3`} type="email" placeholder="Customer email (confirmation copy)" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} data-testid="customer-email-input" />
                    </div>
                  </div>
                  <p className="mt-4 text-xs text-slate-600">Date &amp; time are recorded automatically on submission.</p>

                  {error && <p className="mt-8 text-sm font-semibold text-blaze" data-testid="completion-error">{error}</p>}
                  <button
                    onClick={submit}
                    disabled={submitting}
                    data-testid="confirm-complete-btn"
                    className="mt-10 flex w-full items-center justify-center gap-3 bg-blaze px-8 py-5 text-sm font-bold uppercase tracking-[0.2em] text-white transition-all duration-300 hover:brightness-110 disabled:opacity-50"
                  >
                    {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : "Confirm & Complete Job"}
                  </button>
                </div>
              </>
            )}
          </motion.div>
        )}
      </section>
      <p className="pb-10 text-center text-xs text-slate-600">MoversNearby — Professional Removalists Melbourne &bull; info@moversnearby.com.au</p>
    </main>
  );
}
